import type { StudentProgress } from '../../types'

interface ProgressViewProps {
  progress: StudentProgress
  onBack: () => void
}

export default function ProgressView({ progress, onBack }: ProgressViewProps) {
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    return `${mins} min`
  }

  return (
    <div className="h-full flex flex-col safe-top safe-bottom">
      {/* Header */}
      <header className="px-4 py-3 flex items-center gap-3">
        <button
          onClick={onBack}
          className="p-2 rounded-full bg-white shadow-md hover:shadow-lg transition-shadow"
          aria-label="Go back"
        >
          <span className="text-xl">←</span>
        </button>
        <h1 className="text-2xl font-bold text-flow-dark">Your Progress</h1>
      </header>

      {/* Stats Cards */}
      <main className="flex-1 overflow-y-auto px-4 py-2 no-scrollbar">
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-white rounded-2xl p-4 shadow-md text-center">
            <span className="text-4xl mb-2 block">⭐</span>
            <span className="text-3xl font-bold text-flow-primary">
              {progress.completedAdventures.length}
            </span>
            <span className="text-sm text-gray-600 block">Adventures</span>
          </div>
          <div className="bg-white rounded-2xl p-4 shadow-md text-center">
            <span className="text-4xl mb-2 block">🏆</span>
            <span className="text-3xl font-bold text-flow-secondary">
              {progress.unlockedBadges.length}
            </span>
            <span className="text-sm text-gray-600 block">Badges</span>
          </div>
          <div className="bg-white rounded-2xl p-4 shadow-md text-center">
            <span className="text-4xl mb-2 block">⏱️</span>
            <span className="text-3xl font-bold text-flow-accent">
              {formatTime(progress.totalTimeSpent)}
            </span>
            <span className="text-sm text-gray-600 block">Learning Time</span>
          </div>
          <div className="bg-white rounded-2xl p-4 shadow-md text-center">
            <span className="text-4xl mb-2 block">🔥</span>
            <span className="text-3xl font-bold text-flow-success">
              {progress.currentStreak}
            </span>
            <span className="text-sm text-gray-600 block">Day Streak</span>
          </div>
        </div>

        {/* Adventure History */}
        <h2 className="text-xl font-bold text-flow-dark mb-3">Adventure History</h2>
        {progress.completedAdventures.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <span className="text-4xl block mb-2">🌱</span>
            <p>No adventures yet. Start learning!</p>
          </div>
        ) : (
          <div className="space-y-3 pb-4">
            {progress.completedAdventures.map((adv, i) => (
              <div
                key={`${adv.adventureId}-${i}`}
                className="bg-white rounded-xl p-3 shadow-sm flex items-center justify-between"
              >
                <div>
                  <span className="font-medium text-flow-dark">
                    {adv.adventureId}
                  </span>
                  <span className="text-sm text-gray-500 block">
                    {new Date(adv.completedAt).toLocaleDateString()}
                  </span>
                </div>
                <div className="text-right">
                  <span className="text-lg font-bold text-flow-success">
                    {adv.score}%
                  </span>
                  <span className="text-sm text-gray-500 block">
                    {formatTime(adv.timeSpent)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  )
}
